import { FormManagementQuestionnairesQuery } from 'src/graphql';
import { getLanguageDisplayLabel } from 'src/utils/languages';

export type FormManagementQuestionnaireListItem =
  FormManagementQuestionnairesQuery['formManagementQuestionnaires'][number];

export type QuestionnaireLanguagesSource = {
  languages?: Array<{
    code?: string | null;
    name?: string | null;
  } | null> | null;
};

export function resolveQuestionnaireLanguageDisplayLabel(
  code: string | null | undefined,
  name: string | null | undefined,
): string {
  const trimmedCode = code != null ? String(code).trim() : '';
  if (trimmedCode) {
    const fromCode = getLanguageDisplayLabel(trimmedCode);
    if (fromCode !== trimmedCode) {
      return fromCode;
    }
  }
  const trimmedName = name?.trim();
  if (trimmedName) {
    const fromNameAsCode = getLanguageDisplayLabel(trimmedName);
    if (fromNameAsCode !== trimmedName) {
      return fromNameAsCode;
    }
    return trimmedName;
  }
  return trimmedCode;
}

export function sortLanguageDisplayNames(names: string[]): string[] {
  return [...names].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

export function getCreatedByDisplay(createdBy: unknown): string {
  if (createdBy == null) return '';
  if (typeof createdBy === 'string') return createdBy;
  if (
    typeof createdBy === 'object' &&
    createdBy !== null &&
    'name' in createdBy
  ) {
    return String((createdBy as { name?: string }).name ?? '');
  }
  return '';
}

export function getQuestionnaireLanguageNames(
  item: QuestionnaireLanguagesSource,
): string[] {
  const langs = item.languages;
  if (!langs || !Array.isArray(langs)) return [];
  const names = langs
    .map((l) => {
      if (!l || typeof l !== 'object') return null;
      const code =
        'code' in l ? (l as { code?: string | null }).code : undefined;
      const name =
        'name' in l ? (l as { name?: string | null }).name : undefined;
      const display = resolveQuestionnaireLanguageDisplayLabel(code, name);
      return display.length > 0 ? display : null;
    })
    .filter(
      (name): name is string => typeof name === 'string' && name.length > 0,
    );
  return sortLanguageDisplayNames(names);
}
